import { inject, injectable } from 'inversify'
import { makeObservable, observable, runInAction } from 'mobx'
import { HttpGateway } from '@core/services/http.gateway'
import type { ClockGateway } from '@core/services/clock.gateway'
import { Types } from '@core/ioc/types'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import { MessagePacking, type ActionFailurePm, type ActionResponsePm } from '@core/feedback/feedback.packing'
import { mapReadFailure } from '@core/services/gateway-failure.helpers'
import { ActiveGamesRepository } from './active-games.repository'
import {
  gameStateSchema,
  type BoardSquare,
  type CommandRequest,
  type DrawRequest,
  type GameStatus,
  type Seat,
  type SetupPlacement,
  type StrikeRequest,
  type TileKind,
  type TileSide,
} from '@shared/index'
import { OperationRepository } from '@core/operations/operation.repository'
import type {
  OperationHandle,
  OperationOwner,
  OperationResult,
  ReadonlyOperationState,
} from '@core/operations/operation.types'
import {
  defineOperation,
  operationFailed,
  operationGroup,
  operationId,
  operationKey,
  operationSucceeded,
} from '@core/operations/operation.helpers'

export type LoadGameFailure = ActionFailurePm & Readonly<{ retryable: boolean }>

export type PlacedTilePm = {
  readonly row: number
  readonly col: number
  readonly kind: TileKind
  readonly side: TileSide
  readonly owner: Seat
}

export type GamePm = {
  readonly gameId: string
  readonly code: string
  readonly status: GameStatus
  readonly hostName: string
  // null until the second player has joined — the wire's own sentinel.
  readonly guestName: string | null
  // null for a viewer holding neither seat — a real distinction (onlooker),
  // not a "not yet known" placeholder.
  readonly mySeat: Seat | null
  // A real empty value once the PM exists at all: nothing placed yet is not
  // "not loaded".
  readonly board: readonly PlacedTilePm[]
  readonly hostSetupDone: boolean
  readonly guestSetupDone: boolean
  // All four null until `status` reaches 'playing' — see `gameStateSchema`.
  readonly activeSeat: Seat | null
  readonly winner: Seat | null
  readonly lastMove: Readonly<{ from: BoardSquare; to: BoardSquare }> | null
  readonly bagCount: Readonly<{ host: number; guest: number }> | null
  // Set once `beginDraw` reveals the next bag tile, cleared once `drawTile`
  // places it — null the rest of the time. Only ever the viewer's own; the
  // server never sends the opponent's (`gameStateSchema`'s own comment).
  readonly pendingDraw: Readonly<{ kind: TileKind }> | null
  // null means untimed — set once at creation, never changes after.
  readonly timeControl: Readonly<{ initialSeconds: number; incrementSeconds: number }> | null
  // null whenever `timeControl` is null. Each seat's own remaining time in
  // ms, plus the epoch-ms moment the current turn started (null before
  // `status` reaches 'playing') — `PlayBoardPresenter` ticks a live
  // countdown from this between polls.
  readonly clock: Readonly<{ host: number; guest: number; turnStartedAtMs: number | null }> | null
}

const GAME_GROUP = operationGroup('game')

export const LOAD_GAME_OPERATION = defineOperation<string, LoadGameFailure>({
  id: operationId('game.load'),
  key: (gameId) => operationKey(`load:${gameId}`),
  groups: () => [operationGroup('session'), GAME_GROUP],
  pending: { kind: 'join' },
  safety: { kind: 'read' },
  timeout: {
    kind: 'after',
    milliseconds: 15_000,
    toFailure: () => ({ code: 'temporarily-unavailable' as const, retryable: false }),
  },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

// Never retried: a blind replay would hit the server's own "already set up"
// 409 and report a failure for a write that actually succeeded — the same
// call the lobby's own create/join writes already made.
export const SUBMIT_SETUP_OPERATION = defineOperation<string, ActionFailurePm>({
  id: operationId('game.setup'),
  key: (gameId) => operationKey(`setup:${gameId}`),
  groups: () => [GAME_GROUP],
  pending: { kind: 'ignore' },
  safety: { kind: 'non-idempotent-write' },
  timeout: {
    kind: 'after',
    milliseconds: 10_000,
    toFailure: () => ({ code: 'temporarily-unavailable' as const }),
  },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

// Never retried, same reasoning as SUBMIT_SETUP_OPERATION: a blind replay
// would submit the same move twice.
export const MAKE_MOVE_OPERATION = defineOperation<string, ActionFailurePm>({
  id: operationId('game.move'),
  key: (gameId) => operationKey(`move:${gameId}`),
  groups: () => [GAME_GROUP],
  pending: { kind: 'ignore' },
  safety: { kind: 'non-idempotent-write' },
  timeout: {
    kind: 'after',
    milliseconds: 10_000,
    toFailure: () => ({ code: 'temporarily-unavailable' as const }),
  },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

// Same never-retried reasoning as MAKE_MOVE_OPERATION, one per turn-action
// endpoint (`POST /games/:id/{command,strike,draw}`).
export const COMMAND_TILE_OPERATION = defineOperation<string, ActionFailurePm>({
  id: operationId('game.command'),
  key: (gameId) => operationKey(`command:${gameId}`),
  groups: () => [GAME_GROUP],
  pending: { kind: 'ignore' },
  safety: { kind: 'non-idempotent-write' },
  timeout: { kind: 'after', milliseconds: 10_000, toFailure: () => ({ code: 'temporarily-unavailable' as const }) },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

export const STRIKE_TILE_OPERATION = defineOperation<string, ActionFailurePm>({
  id: operationId('game.strike'),
  key: (gameId) => operationKey(`strike:${gameId}`),
  groups: () => [GAME_GROUP],
  pending: { kind: 'ignore' },
  safety: { kind: 'non-idempotent-write' },
  timeout: { kind: 'after', milliseconds: 10_000, toFailure: () => ({ code: 'temporarily-unavailable' as const }) },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

export const DRAW_TILE_OPERATION = defineOperation<string, ActionFailurePm>({
  id: operationId('game.draw'),
  key: (gameId) => operationKey(`draw:${gameId}`),
  groups: () => [GAME_GROUP],
  pending: { kind: 'ignore' },
  safety: { kind: 'non-idempotent-write' },
  timeout: { kind: 'after', milliseconds: 10_000, toFailure: () => ({ code: 'temporarily-unavailable' as const }) },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

// Reveals the next bag tile without placing it — the square is chosen
// afterward, via `DRAW_TILE_OPERATION`. Same never-retried reasoning: a
// blind replay would hit the server's own idempotent "already revealed"
// response, which is harmless, but retrying here would still be racing the
// player's own read of the result.
export const BEGIN_DRAW_OPERATION = defineOperation<string, ActionFailurePm>({
  id: operationId('game.draw-begin'),
  key: (gameId) => operationKey(`draw-begin:${gameId}`),
  groups: () => [GAME_GROUP],
  pending: { kind: 'ignore' },
  safety: { kind: 'non-idempotent-write' },
  timeout: { kind: 'after', milliseconds: 10_000, toFailure: () => ({ code: 'temporarily-unavailable' as const }) },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

// A background poll for the opponent's own setup progress — a tick firing
// while the previous one is still in flight is dropped, not joined, the same
// reasoning as the lobby's REFRESH_GAME_OPERATION.
export const REFRESH_GAME_OPERATION = defineOperation<string, ActionFailurePm>({
  id: operationId('game.refresh'),
  key: (gameId) => operationKey(`refresh:${gameId}`),
  groups: () => [GAME_GROUP],
  pending: { kind: 'ignore' },
  safety: { kind: 'read' },
  timeout: {
    kind: 'after',
    milliseconds: 10_000,
    toFailure: () => ({ code: 'temporarily-unavailable' as const }),
  },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

const GAME_POLL_INTERVAL_MS = 3_000

/**
 * The live game once both lobby seats are filled: the board, both seats'
 * setup progress, and the poll that reveals the opponent's own placement.
 * Not `LobbyRepository`, which this Repository could otherwise be mistaken
 * for a near-duplicate of — that one pairs exactly two sessions onto one
 * game and its whole job ends at "both seats filled"; this one starts there.
 * It deliberately does not import `LobbyRepository`: the seat this viewer
 * holds comes from the server's own `yourSeat` on every read, so a hard
 * reload of `/game/:gameId/setup` needs nothing the lobby ever held.
 */
@injectable()
export class GameRepository implements UserScopedState {
  gamePm: GamePm | null = null

  // Lets `loadState` be readable (via `stateFor`) before the PM exists —
  // e.g. while the very first load is still in flight.
  currentGameId: string | null = null

  private readonly dataGateway: HttpGateway
  private readonly clockGateway: ClockGateway
  private readonly activeGamesRepository: ActiveGamesRepository
  private readonly operationOwner: OperationOwner
  private readonly loadOperation: OperationHandle<string, LoadGameFailure>
  private readonly submitSetupOperation: OperationHandle<string, ActionFailurePm>
  private readonly makeMoveOperation: OperationHandle<string, ActionFailurePm>
  private readonly commandTileOperation: OperationHandle<string, ActionFailurePm>
  private readonly strikeTileOperation: OperationHandle<string, ActionFailurePm>
  private readonly drawTileOperation: OperationHandle<string, ActionFailurePm>
  private readonly beginDrawOperation: OperationHandle<string, ActionFailurePm>
  private readonly refreshOperation: OperationHandle<string, ActionFailurePm>

  // Non-null only while a poll is running — the disposer doubles as the "am
  // I currently watching" flag, same as LobbyRepository's.
  private stopPoll: (() => void) | null = null

  constructor(
    @inject(Types.IDataGateway) dataGateway: HttpGateway,
    @inject(Types.IClockGateway) clockGateway: ClockGateway,
    @inject(ActiveGamesRepository) activeGamesRepository: ActiveGamesRepository,
    @inject(OperationRepository) operationRepository: OperationRepository,
  ) {
    this.dataGateway = dataGateway
    this.clockGateway = clockGateway
    this.activeGamesRepository = activeGamesRepository
    this.operationOwner = operationRepository.createOwner({ id: 'game.repository', lifetime: 'session' })
    this.loadOperation = this.operationOwner.register(LOAD_GAME_OPERATION)
    this.submitSetupOperation = this.operationOwner.register(SUBMIT_SETUP_OPERATION)
    this.makeMoveOperation = this.operationOwner.register(MAKE_MOVE_OPERATION)
    this.commandTileOperation = this.operationOwner.register(COMMAND_TILE_OPERATION)
    this.strikeTileOperation = this.operationOwner.register(STRIKE_TILE_OPERATION)
    this.drawTileOperation = this.operationOwner.register(DRAW_TILE_OPERATION)
    this.beginDrawOperation = this.operationOwner.register(BEGIN_DRAW_OPERATION)
    this.refreshOperation = this.operationOwner.register(REFRESH_GAME_OPERATION)

    makeObservable(this, {
      gamePm: observable.ref,
      currentGameId: observable.ref,
    })
  }

  get isLoaded(): boolean {
    return this.gamePm !== null
  }

  get loadState(): ReadonlyOperationState<LoadGameFailure> {
    return this.loadOperation.stateFor(this.currentGameId ?? '')
  }

  get isSubmittingSetup(): boolean {
    return this.submitSetupOperation.stateFor(this.currentGameId ?? '').status === 'loading'
  }

  get isMovingTile(): boolean {
    return this.makeMoveOperation.stateFor(this.currentGameId ?? '').status === 'loading'
  }

  /** True while any of the four turn-action endpoints is in flight — what the board disables itself for. */
  get isSubmittingTurn(): boolean {
    const key = this.currentGameId ?? ''
    return (
      this.makeMoveOperation.stateFor(key).status === 'loading' ||
      this.commandTileOperation.stateFor(key).status === 'loading' ||
      this.strikeTileOperation.stateFor(key).status === 'loading' ||
      this.drawTileOperation.stateFor(key).status === 'loading'
    )
  }

  /**
   * Always a real fetch, not `loadIfNeeded` — a live game must reflect the
   * server the moment the route is entered, not a cached read gated by
   * `FreshnessRepository` (which bounds a list's staleness, not a poll's
   * subject).
   */
  load = async (gameId: string): Promise<ActionResponsePm> => {
    this.currentGameId = gameId

    const result = await this.loadOperation.run(
      gameId,
      async ({ signal }) => {
        const response = await this.dataGateway.get(`/games/${encodeURIComponent(gameId)}`, {
          signal,
          idempotencyKey: null,
        })
        if (!response.success) return operationFailed({ ...mapReadFailure(response), retryable: true })

        const parsed = gameStateSchema.safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'invalid-external-data', retryable: false })

        return operationSucceeded(parsed.data)
      },
      {
        succeeded: (game) => {
          this.gamePm = toGamePm(game)
          // The poll reveals the opponent's own setup progress while
          // 'ready', and their own moves while 'playing' — it only has
          // nothing left to reveal once the game is 'finished'.
          if (game.status === 'ready' || game.status === 'playing') this.startWatching()
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  /**
   * Submits the whole atomic placement (Duke + two Footmen). No optimistic
   * write: the referee's own board, returned here, replaces the PM outright,
   * so there is no rollback path to maintain.
   */
  submitSetup = async (placement: SetupPlacement): Promise<ActionResponsePm> => {
    const gameId = this.currentGameId
    if (gameId === null) return MessagePacking.cancelled()

    const result = await this.submitSetupOperation.run(
      gameId,
      async ({ signal }) => {
        const response = await this.dataGateway.post(`/games/${encodeURIComponent(gameId)}/setup`, placement, {
          signal,
          idempotencyKey: null,
        })
        if (!response.success) return operationFailed(mapReadFailure(response))

        const parsed = gameStateSchema.safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'invalid-external-data' })

        return operationSucceeded(parsed.data)
      },
      {
        succeeded: (game) => {
          this.gamePm = toGamePm(game)
          if (game.status === 'finished') this.stopWatching()
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  /**
   * Submits one turn (`from` -> `to`). Same no-optimistic-write shape as
   * `submitSetup`: the referee's own resulting board (and, once the game
   * ends, `status`/`winner`) replaces the PM outright.
   */
  makeMove = async (from: BoardSquare, to: BoardSquare): Promise<ActionResponsePm> => {
    const gameId = this.currentGameId
    if (gameId === null) return MessagePacking.cancelled()

    const result = await this.makeMoveOperation.run(
      gameId,
      async ({ signal }) => {
        const response = await this.dataGateway.post(
          `/games/${encodeURIComponent(gameId)}/move`,
          { from, to },
          {
            signal,
            idempotencyKey: null,
          },
        )
        if (!response.success) return operationFailed(mapReadFailure(response))

        const parsed = gameStateSchema.safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'invalid-external-data' })

        return operationSucceeded(parsed.data)
      },
      {
        succeeded: (game) => {
          this.gamePm = toGamePm(game)
          if (game.status === 'finished') this.stopWatching()
          // Refreshes the sidebar's active-games list right away — the turn
          // just changed, no reason to wait for its own separate poll interval.
          void this.activeGamesRepository.checkNow()
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  /** Submits one Command turn — same no-optimistic-write shape as `makeMove`. */
  commandTile = async (command: CommandRequest): Promise<ActionResponsePm> => {
    const gameId = this.currentGameId
    if (gameId === null) return MessagePacking.cancelled()

    const result = await this.commandTileOperation.run(
      gameId,
      async ({ signal }) => {
        const response = await this.dataGateway.post(`/games/${encodeURIComponent(gameId)}/command`, command, {
          signal,
          idempotencyKey: null,
        })
        if (!response.success) return operationFailed(mapReadFailure(response))

        const parsed = gameStateSchema.safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'invalid-external-data' })

        return operationSucceeded(parsed.data)
      },
      {
        succeeded: (game) => {
          this.gamePm = toGamePm(game)
          if (game.status === 'finished') this.stopWatching()
          // Refreshes the sidebar's active-games list right away — the turn
          // just changed, no reason to wait for its own separate poll interval.
          void this.activeGamesRepository.checkNow()
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  /** Submits one Strike turn — same no-optimistic-write shape as `makeMove`. */
  strikeTile = async (strike: StrikeRequest): Promise<ActionResponsePm> => {
    const gameId = this.currentGameId
    if (gameId === null) return MessagePacking.cancelled()

    const result = await this.strikeTileOperation.run(
      gameId,
      async ({ signal }) => {
        const response = await this.dataGateway.post(`/games/${encodeURIComponent(gameId)}/strike`, strike, {
          signal,
          idempotencyKey: null,
        })
        if (!response.success) return operationFailed(mapReadFailure(response))

        const parsed = gameStateSchema.safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'invalid-external-data' })

        return operationSucceeded(parsed.data)
      },
      {
        succeeded: (game) => {
          this.gamePm = toGamePm(game)
          if (game.status === 'finished') this.stopWatching()
          // Refreshes the sidebar's active-games list right away — the turn
          // just changed, no reason to wait for its own separate poll interval.
          void this.activeGamesRepository.checkNow()
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  /** Reveals the next bag tile without placing it — same no-optimistic-write shape as `makeMove`. */
  beginDraw = async (): Promise<ActionResponsePm> => {
    const gameId = this.currentGameId
    if (gameId === null) return MessagePacking.cancelled()

    const result = await this.beginDrawOperation.run(
      gameId,
      async ({ signal }) => {
        const response = await this.dataGateway.post(
          `/games/${encodeURIComponent(gameId)}/draw/begin`,
          {},
          { signal, idempotencyKey: null },
        )
        if (!response.success) return operationFailed(mapReadFailure(response))

        const parsed = gameStateSchema.safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'invalid-external-data' })

        return operationSucceeded(parsed.data)
      },
      {
        succeeded: (game) => {
          this.gamePm = toGamePm(game)
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  /** Submits one Draw turn — same no-optimistic-write shape as `makeMove`. */
  drawTile = async (draw: DrawRequest): Promise<ActionResponsePm> => {
    const gameId = this.currentGameId
    if (gameId === null) return MessagePacking.cancelled()

    const result = await this.drawTileOperation.run(
      gameId,
      async ({ signal }) => {
        const response = await this.dataGateway.post(`/games/${encodeURIComponent(gameId)}/draw`, draw, {
          signal,
          idempotencyKey: null,
        })
        if (!response.success) return operationFailed(mapReadFailure(response))

        const parsed = gameStateSchema.safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'invalid-external-data' })

        return operationSucceeded(parsed.data)
      },
      {
        succeeded: (game) => {
          this.gamePm = toGamePm(game)
          if (game.status === 'finished') this.stopWatching()
          // Refreshes the sidebar's active-games list right away — the turn
          // just changed, no reason to wait for its own separate poll interval.
          void this.activeGamesRepository.checkNow()
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  /**
   * One poll tick: re-fetches the game and stops the poll once it ends.
   * A no-op (`MessagePacking.cancelled`) when there is no game to check
   * rather than a thrown error — a stray tick can outrace `leaveGame()` by a
   * beat, the same reasoning as `LobbyRepository.checkForOpponent`.
   */
  refreshGameState = async (): Promise<ActionResponsePm> => {
    const gameId = this.currentGameId
    if (gameId === null || this.gamePm === null) return MessagePacking.cancelled()

    const result = await this.refreshOperation.run(
      gameId,
      async ({ signal }) => {
        const response = await this.dataGateway.get(`/games/${encodeURIComponent(gameId)}`, {
          signal,
          idempotencyKey: null,
        })
        if (!response.success) return operationFailed(mapReadFailure(response))

        const parsed = gameStateSchema.safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'invalid-external-data' })

        return operationSucceeded(parsed.data)
      },
      {
        succeeded: (game) => {
          // Every field comes straight from the DTO — the seat now arrives
          // as `yourSeat` on the wire, so unlike the lobby's own poll there
          // is no "preserve the old isHost" special case needed here.
          this.gamePm = toGamePm(game)
          if (game.status === 'finished') this.stopWatching()
          // Same reasoning as the four turn-action callbacks above: the
          // player sitting here waiting (not the one who just moved) has no
          // "I just did this" hook of their own — this poll tick is the
          // first moment *their* session learns the turn changed, so it
          // refreshes the sidebar too instead of waiting for its own
          // separate, slower interval.
          void this.activeGamesRepository.checkNow()
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  /** Registers the poll without an immediate first check — `load`'s own response already is that first read. */
  private startWatching(): void {
    if (this.stopPoll !== null) return
    this.stopPoll = this.clockGateway.everyInterval(GAME_POLL_INTERVAL_MS, () => {
      void this.refreshGameState()
    })
  }

  private stopWatching(): void {
    this.stopPoll?.()
    this.stopPoll = null
  }

  /** Leaving the setup screen — called from the route's own `onLeave`. */
  leaveGame = (): void => {
    this.stopWatching()
    runInAction(() => {
      this.gamePm = null
      this.currentGameId = null
    })
  }

  resetUserScopedState = (): void => {
    this.stopWatching()
    this.operationOwner.resetAll({ kind: 'session-reset' })
    runInAction(() => {
      this.gamePm = null
      this.currentGameId = null
    })
  }

  private toActionResponsePm(result: OperationResult<unknown, ActionFailurePm>): ActionResponsePm {
    switch (result.kind) {
      case 'succeeded':
        return MessagePacking.succeeded()
      case 'failed':
        return { success: false, failure: result.error }
      case 'cancelled':
      case 'skipped':
        return MessagePacking.cancelled()
    }
  }
}

/** DTO → PM defined copy, field by field (CI-4, CI-10) — never a reference to the parsed DTO. */
function toGamePm(game: {
  gameId: string
  code: string
  status: GameStatus
  hostName: string
  guestName: string | null
  board: readonly { row: number; col: number; kind: TileKind; side: TileSide; owner: Seat }[]
  hostSetupDone: boolean
  guestSetupDone: boolean
  yourSeat: Seat | null
  activeSeat: Seat | null
  winner: Seat | null
  lastMove: { from: { row: number; col: number }; to: { row: number; col: number } } | null
  bagCount: { host: number; guest: number } | null
  pendingDraw: { kind: TileKind } | null
  timeControl: { initialSeconds: number; incrementSeconds: number } | null
  clock: { host: number; guest: number; turnStartedAtMs: number | null } | null
}): GamePm {
  return Object.freeze({
    gameId: game.gameId,
    code: game.code,
    status: game.status,
    hostName: game.hostName,
    guestName: game.guestName,
    mySeat: game.yourSeat,
    board: game.board.map((tile) =>
      Object.freeze({ row: tile.row, col: tile.col, kind: tile.kind, side: tile.side, owner: tile.owner }),
    ),
    hostSetupDone: game.hostSetupDone,
    guestSetupDone: game.guestSetupDone,
    activeSeat: game.activeSeat,
    winner: game.winner,
    lastMove:
      game.lastMove === null
        ? null
        : Object.freeze({
            from: Object.freeze({ row: game.lastMove.from.row, col: game.lastMove.from.col }),
            to: Object.freeze({ row: game.lastMove.to.row, col: game.lastMove.to.col }),
          }),
    bagCount: game.bagCount === null ? null : Object.freeze({ host: game.bagCount.host, guest: game.bagCount.guest }),
    pendingDraw: game.pendingDraw === null ? null : Object.freeze({ kind: game.pendingDraw.kind }),
    timeControl:
      game.timeControl === null
        ? null
        : Object.freeze({
            initialSeconds: game.timeControl.initialSeconds,
            incrementSeconds: game.timeControl.incrementSeconds,
          }),
    clock:
      game.clock === null
        ? null
        : Object.freeze({
            host: game.clock.host,
            guest: game.clock.guest,
            turnStartedAtMs: game.clock.turnStartedAtMs,
          }),
  })
}
